import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { usePostHog } from '@posthog/react';
import styled from 'styled-components';
import ServiceBox from "./components/service-box";
import MobileTuneUpsSelect from "./components/MobileTuneUpsSelect";
import IndividualBox from "./components/individual-box";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Privacy from "./Privacy";
import Terms from "./Terms";
import BikesForSale from "./pages/BikesForSale";
import Admin from "./pages/Admin";
import Book from "./pages/Book";
import PageSeo from './components/PageSeo';
import AwardBadge from './components/AwardBadge';
import { useLocale } from './i18n/LocaleContext';
import TuneUp from './images/close-up-hand-repairing-bike.jpg';
import { useThemeMode } from "./ThemeModeContext";

const PageWrapper = styled.div`
  margin: 0 auto;
  padding: 0;
`;

const SiteDescription = styled.section`
  font-size: 1.0625rem;
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin: 0 auto;
  padding: 2rem 1.5rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text};

  h1 {
    font-size: clamp(1.35rem, 2.5vw, 2rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 1rem;
    color: ${({ theme }) => theme.colors.text};
  }

  @media screen and (min-width: 1350px) {
    padding-top: 2.5rem;
  }
`;

const ServiceCallout = styled.p`
  margin: 1.25rem 0 0;
  padding: 1rem 1.15rem;
  font-size: 0.98rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.bgMuted};
  border-radius: ${({ theme }) => theme.radius.md};
  border-left: 3px solid ${({ theme }) => theme.colors.accent};
`;

const RegularMaintenance = styled.section`
  display: flex;
  flex-direction: column;
  font-size: 1.0625rem;
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin: 0 auto;
  padding: 2rem 1.5rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text};

  h2 {
    margin-top: 2.5rem;
    font-size: clamp(1.35rem, 2vw, 1.75rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    color: ${({ theme }) => theme.colors.text};
  }

  @media screen and (min-width: 1100px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;

    h2 {
      margin-top: 0;
    }
  }

  img {
    width: calc(100% + 3rem);
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    object-fit: cover;
    border-radius: 0;
    box-shadow: none;

    @media screen and (min-width: 1100px) {
      flex: 0 0 min(42%, 480px);
      width: 100%;
      margin: 0;
      padding-right: 0;
      border-radius: ${({ theme }) => theme.radius.md};
      box-shadow: ${({ theme }) => theme.shadow.md};
    }
  }
`;

const ServiceHeader = styled.h2`
  margin: 0;
  text-align: center;
  padding: 2rem 1rem 0;
  font-size: clamp(1.5rem, 2.5vw, 1.85rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text};
`;

const MyServices = styled.section`
  margin: 0 auto;
  padding: 1rem 0.75rem 0;
  max-width: 1325px;
  font-size: 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
`;

const IndividualServices = styled.section`
  display: grid;
  justify-content: center;
  align-items: stretch;
  margin: 0 auto;
  padding: 1rem 0.75rem 0;
  width: 100%;
  max-width: 68rem;
  gap: 0.5rem;

  @media screen and (min-width: 1000px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 1rem;
    row-gap: 0.5rem;
  }
`;

const BreakLine = styled.div`
  width: min(280px, 40%);
  background: linear-gradient(
    90deg,
    transparent,
    ${({ theme }) => theme.colors.borderStrong},
    transparent
  );
  height: 1px;
  padding: 0;
  margin: 4rem auto 0.5rem;
`;

/** Hero already ends with padding, so the divider above reviews needs no extra top margin. */
const ReviewsBreakLine = styled(BreakLine)`
  margin: 0 auto;
`;

const ReviewsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1.5rem 1rem;
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin: 0 auto;

  h2 {
    font-size: clamp(1.35rem, 2.5vw, 1.85rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
    /* Reviews widget adds its own 10px top padding */
    margin: 0 0 0.25rem;
    color: ${({ theme }) => theme.colors.text};
    text-align: center;
  }

  [data-bikeops-reviews] {
    width: 100%;
  }
`;

const MyEmail = styled.div`
  padding: 3rem 1.5rem 2rem;
  font-size: 1.125rem;
  text-align: center;
  line-height: 1.85;
  color: ${({ theme }) => theme.colors.text};

  a {
    color: ${({ theme }) => theme.colors.footerLink};
    text-decoration: none;
    font-weight: 500;
    text-underline-offset: 3px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LocationMap = styled.div`
  width: min(100%, 42rem);
  aspect-ratio: 16 / 9;
  margin: 1.25rem auto 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.sm};
  box-shadow: ${({ theme }) => theme.shadow.md};

  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const AboutSection = styled.section`
  max-width: ${({ theme }) => theme.maxWidth.content};
  margin: 0 auto;
  padding: 2rem 1.5rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.0625rem;

  h2 {
    font-size: clamp(1.35rem, 2vw, 1.75rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0 0 1rem;
    color: ${({ theme }) => theme.colors.text};
  }

  p {
    margin: 0 0 1rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const SmsDisclosure = styled.section`
  max-width: ${({ theme }) => theme.maxWidth.prose};
  margin: 0 auto;
  padding: 2rem 1.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 0.9rem;
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.textMuted};

  h2 {
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    margin: 0 0 0.65rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  a {
    color: ${({ theme }) => theme.colors.footerLink};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }
`;


const ViewButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem;
  margin-top: 0.5rem;

  button {
    margin: 0;
    background-color: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    padding: 0.55rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    font-family: inherit;
    color: ${({ theme }) => theme.colors.text};
    cursor: pointer;
    border-radius: ${({ theme }) => theme.radius.full};
    transition: background ${({ theme }) => theme.transition}, border-color ${({ theme }) => theme.transition},
      box-shadow ${({ theme }) => theme.transition};

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }

  .active {
    background-color: ${({ theme }) => theme.colors.accentMuted};
    border-color: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.text};
    box-shadow: ${({ theme }) => theme.shadow.sm};
  }
`;

function PageViewTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      site: 'basementbikemechanic',
    });
  }, [location, posthog]);

  return null;
}

function ReviewsWidget() {
  const hostRef = useRef(null);
  const { mode } = useThemeMode();

  useEffect(() => {
    const host = hostRef.current;

    if (!host) return undefined;

    host.replaceChildren();

    const widgetMount = document.createElement("div");
    widgetMount.setAttribute("data-bikeops-reviews", "");
    host.appendChild(widgetMount);

    const script = document.createElement("script");
    script.src = "https://bbm.bikeops.co/reviews-widget.js";
    script.async = true;
    script.setAttribute("data-base-url", "https://bbm.bikeops.co");
    script.setAttribute("data-theme", mode);

    host.appendChild(script);

    return () => {
      host.replaceChildren();
    };
  }, [mode]);

  return <div ref={hostRef} />;
}

function HomePage({ width, view, switchViews }) {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const { t, siteConfig: config, seo } = useLocale();
  const mapQuery = encodeURIComponent("2272 Melinda Dr NE, Atlanta GA 30345");
  return (
    <PageWrapper>
      <PageSeo title={seo.defaultTitle} description={seo.defaultDescription} path="" />
      <Header />
      <SiteDescription>
        <h1>{t("home.heroTitle")}</h1>
        {config.site_description}
        <ServiceCallout>
          {t("home.disclaimer")}
        </ServiceCallout>
        <AwardBadge />
      </SiteDescription>
      <ReviewsBreakLine />
      <ReviewsSection>
        <h2>{t("home.reviewsHeading")}</h2>
        <ReviewsWidget />
      </ReviewsSection>
      <BreakLine />
      <AboutSection id="about">
        <h2>{t("home.aboutHeading")}</h2>
        <p>{t("home.aboutP1")}</p>
        <p>{t("home.aboutP2")}</p>
        <p>{t("home.aboutP3")}</p>
      </AboutSection>
      <RegularMaintenance>
        <img
          src={TuneUp}
          alt={t("home.tuneUpImageAlt")}
        />
        <div>
          <h2>{t("home.maintenanceHeading")}</h2>
          <p>{config.regular_maintenance_first}</p>
          <p>{config.regular_maintenance_second}</p>
        </div>
      </RegularMaintenance>
      {width <= 1000 ? (
        <>
          <ViewButton>
            <button onClick={() => { posthog.capture('service_tab_switched', { tab: 'tuneup' }); switchViews('tuneup'); }} className={view === 'tuneup' ? 'active' : undefined}>{t("home.tabTuneUps")}</button><button onClick={() => { posthog.capture('service_tab_switched', { tab: 'service' }); switchViews('service'); }} className={view === 'service' ? 'active' : undefined}>{t("home.tabServices")}</button>
          </ViewButton>
          {view === 'tuneup' && (
            <>
              <ServiceHeader>{ config.service_header }</ServiceHeader>
              <MobileTuneUpsSelect services={config.services} />
            </>
          )}
          {view === 'service' && (
          <>
            <ServiceHeader>{ config.additional_services }</ServiceHeader>
            <IndividualServices>
              {config.individual_services.map(item => <IndividualBox services={item} key={item.id} />)}
            </IndividualServices>
          </>
          )}
        </>
      ) : (
        <>
          <ServiceHeader>{ config.service_header }</ServiceHeader>
          <MyServices>
            {config.services.map(item => <ServiceBox services={item} key={item.id} />)}
          </MyServices>
          <BreakLine/>
          <ServiceHeader>{ config.additional_services }</ServiceHeader>
          <IndividualServices>
            {config.individual_services.map(item => <IndividualBox services={item} key={item.id} />)}
          </IndividualServices>
        </>
      )}
      <MyEmail>
        {t("home.contactText")} <a href={`tel:${config.phone}`} onClick={() => posthog.capture('contact_clicked', { method: 'phone' })}>{config.phone}</a><br />
        {t("home.contactEmail")} <a href={`mailto:${config.email}`} onClick={() => posthog.capture('contact_clicked', { method: 'email' })}>{config.email}</a><br />
        {t("home.contactLocation")} <a href="https://maps.app.goo.gl/dPsymJhVVwD5ymha6" onClick={() => posthog.capture('contact_clicked', { method: 'maps' })}>2272 Melinda Dr NE, Atlanta GA 30345</a>
        <LocationMap>
          <iframe
            title={t("home.mapTitle")}
            src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </LocationMap>
      </MyEmail>
      <SmsDisclosure>
        <h2>{t("home.smsHeading")}</h2>
        <p>{t("home.smsP1")}</p>
        <p>{t("home.smsP2")}</p>
        <p>{t("home.smsP3")}</p>
        <p>
          {t("home.smsViewOur")}{' '}
          <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>{t("footer.privacyPolicy")}</a>
          {' '}{t("home.smsAnd")}{' '}
          <a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }}>{t("home.smsTermsOfService")}</a>.
        </p>
      </SmsDisclosure>
      <Footer onNavigatePrivacy={() => navigate('/privacy')} onNavigateTerms={() => navigate('/terms')} />
    </PageWrapper>
  );
}

const App = () => {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [view, setView] = useState('tuneup');

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    // window.dataLayer = window.dataLayer || [];

    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, []);

  function switchViews(newView) {
    if (newView === 'tuneup') {
      setView('tuneup');
    } else {
      setView('service');
    }
  }

  // function gtag(){window.dataLayer.push(arguments);}
  // gtag('js', new Date());

  // gtag('config', 'AW-16578987654');

  // // record page view
  // gtag('event', 'conversion', {'send_to': 'AW-16578987654/JcCuCIuT27gZEIaNveE9'});

  // function gtag_report_conversion(url) {
  //   gtag('event', 'conversion', {
  //       'send_to': 'AW-16578987654/P1sJCJPchsUZEIaNveE9',
  //       'event_callback': ''
  //   });
  //   return false;
  // }

  return (
    <>
      <PageViewTracker />
      <Routes>
      <Route path="/admin" element={<Admin />} />
      <Route path="/book" element={<Book />} />
      <Route path="/bikes-for-sale" element={<BikesForSale />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/" element={<HomePage width={width} view={view} switchViews={switchViews} />} />
      </Routes>
    </>
  )
}

export default App;
